import { useEffect, useState } from 'react'
import VsScene from '../components/VsScene.jsx'
import ReadAloud from '../components/ReadAloud.jsx'
import { play } from '../lib/sounds.js'

/**
 * SPEC screen 3: the two animals face off, a big Fight! button, stat rows
 * animate in one at a time, each row highlights the side with the edge.
 *
 * The rows are drawn from roster.json the instant the kid taps Fight (D-071), so
 * this screen no longer waits on the model. The Weapon and Armor edges need
 * combat scores the client never sees, so they arrive with the story and land as
 * `edge: null` until then — the row shows, unhighlighted, and the existing 420ms
 * stagger absorbs the late highlight.
 *
 * Every figure is stated plainly (D-118). SPEC Section 8's "scientists think"
 * badge on an estimated stat was built in D-087 and dropped with the rest of the
 * hedge: a dinosaur's weight reads the way a lion's does, and read-aloud says
 * the numbers as they are.
 *
 * When a storybook animal (era "legend", D-113) is in the fight, the era note
 * opens with a book instead of an hourglass: it never lived, so "different
 * times" would be the wrong story.
 *
 * The call (D-135): at ages 4 and 6 the two animals in the scene are buttons —
 * tap one to say who will win, before the facts. It is never required: Fight!
 * is live with no call made (invariant 11), and at age 2 the screen is exactly
 * what it was. A tap plays the animal's sound, the way a roster pick does.
 */
export default function Matchup({ story, A, B, age, call = null, onCall = null, onFight }) {
  const [shown, setShown] = useState(0)
  useEffect(() => {
    // Reveal rows one at a time; the last timer always lands on the full count.
    setShown(0)
    const rows = story.stat_comparison.length
    const timers = Array.from({ length: rows }, (_, n) => setTimeout(() => setShown(n + 1), 420 * (n + 1)))
    return () => timers.forEach(clearTimeout)
  }, [story])

  const legendary = A.era === 'legend' || B.era === 'legend'
  const rowsText = story.stat_comparison.map((r) => `${r.label}: ${A.name} ${r.a}, ${B.name} ${r.b}.`).join(' ')

  const calling = age !== '2' && !!onCall
  const called = call === A.slug ? A : call === B.slug ? B : null
  const callPrompt = called ? `Your call: ${called.name}` : 'Who do you think wins? Tap one.'
  const makeCall = (slug) => {
    if (slug !== call) play((slug === A.slug ? A : B).sound)
    onCall(slug)
  }

  return (
    <section className="screen matchup">
      <h1 className="matchup__title">{story.title}</h1>
      <div className="matchup__scene">
        <VsScene a={A} b={B} setting={story.setting} call={calling ? call : null} onCall={calling ? makeCall : null} />
        {calling && <p className={`matchup__call${called ? ' is-called' : ''}`} aria-live="polite">{callPrompt}</p>}
      </div>
      <div className="matchup__col">
      <ReadAloud text={story.intro} extraSpoken={`${rowsText}${calling && !called ? ' Who do you think wins? Tap one.' : ''}`} auto={age !== '6'} className="matchup__intro" />
      <table className="stats" aria-label="Stat comparison">
        <thead>
          <tr><th className="stats__a">{A.name}</th><th></th><th className="stats__b">{B.name}</th></tr>
        </thead>
        <tbody>
          {story.stat_comparison.map((r, i) => (
            <tr key={r.label} className={`stats__row${i < shown ? ' is-in' : ''}${r.edge ? ` edge--${r.edge}` : ''}`}>
              <td className="stats__a">{r.a}</td>
              <td className="stats__label">{r.label}</td>
              <td className="stats__b">{r.b}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {story.cross_era_note && <p className="era-note">{legendary ? '📖' : '⏳'} {story.cross_era_note}</p>}
      </div>
      <button className="btn btn--fight btn--huge" onClick={onFight}>Fight!</button>
    </section>
  )
}
